import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { CreditCardIcon, Loader2, SaveIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Drawer } from 'vaul';

import { FloatingPortalProvider } from '@/components/ui/floating-portal';

import { ConfirmDeleteDialog } from '@/components/app/confirm-delete-dialog';
import { isTimeBlockEvent, useCalendar } from '@/components/calendar';
import { getCalendarEventColor } from '@/components/calendar/event-colors';
import { useCalendarColorBy } from '@/components/calendar/hooks/use-calendar-color-by';
import { useUpdateEvent } from '@/components/calendar/hooks/use-update-event';
import { reportDeleteEventFailure } from '@/components/calendar/report-delete-event-failure';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AppointmentQuickActions,
  AppointmentStatusProgression,
  DepositBadge,
  DepositDetailRow,
  DepositLinkActions,
  appointmentStatusLabels,
  useLiveBooking,
} from '@/features/appointments';
import { AppointmentClientLink } from '@/features/appointments/components/appointment-client-link';
import {
  AppointmentEditFields,
  type AppointmentEditFormData,
  applyAppointmentEdit,
  appointmentEditDefaults,
  appointmentEditSchema,
  appointmentEditStaffOptions,
  appointmentEventDuration,
} from '@/features/appointments/edit';
import { AppointmentFormsPanel } from '@/features/consent-forms/components/appointment-forms-panel';
import { useCheckoutStore } from '@/features/sales';
import {
  MobileBlockedTimeForm,
  blockedTimeTargetFromEvent,
} from '@/features/scheduling';
import { zonedEvent } from '@/lib/timezone';
import { cn } from '@/lib/utils';

import type { IEvent } from '@/components/calendar/interfaces';

interface MobileBookingDetailSheetProps {
  event: IEvent;
  children: React.ReactNode;
}

/**
 * Mobile-only detail/edit bottom sheet. Conforms to
 * ICalendarConfig.customEventDetailsDialog — opened by tapping an event.
 *
 * Time BLOCKS route to the SHARED blocked-time editor (edit + delete, with the
 * type preset, multi-practitioner selection and recurrence). Appointments get an
 * explicit SAVE — the old sheet fire-and-forgot every field change, so a save
 * the server rejected left the stale value on screen — plus the same status
 * progression, no-show, quick actions and CHECKOUT the desktop side-panel has.
 */
export function MobileBookingDetailSheet({
  event,
  children,
}: MobileBookingDetailSheetProps) {
  const [open, setOpen] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(
    null
  );

  return (
    <Drawer.Root open={open} onOpenChange={setOpen}>
      <Drawer.Trigger asChild>{children}</Drawer.Trigger>
      <FloatingPortalProvider container={portalContainer}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 z-[100] bg-black/40" />
          <Drawer.Content
            ref={setPortalContainer}
            className="fixed inset-x-0 bottom-0 z-[100] flex max-h-[92dvh] flex-col overflow-hidden rounded-t-[22px] border-t border-border bg-background pb-[max(16px,env(safe-area-inset-bottom,0px))] pt-2.5 outline-none"
          >
            <div className="flex shrink-0 flex-col items-center pt-0.5 pb-1">
              <div
                className="h-1 w-8 shrink-0 rounded-full bg-muted-foreground/30"
                aria-hidden
              />
            </div>
            {open &&
              (isTimeBlockEvent(event) ? (
                <BlockedTimeBody event={event} onClose={() => setOpen(false)} />
              ) : (
                <AppointmentBody event={event} onClose={() => setOpen(false)} />
              ))}
          </Drawer.Content>
        </Drawer.Portal>
      </FloatingPortalProvider>
    </Drawer.Root>
  );
}

// =============================================================================
// Time block — shared blocked-time editor (edit + delete)
// =============================================================================

function BlockedTimeBody({
  event,
  onClose,
}: {
  event: IEvent;
  onClose: () => void;
}) {
  const [isSaving, setIsSaving] = useState(false);
  const { timeZone } = useCalendar();
  const formId = 'mobile-edit-block';

  if (event.metadata?.importedFromGoogle || event.metadata?.calendarSourceId) {
    return (
      <div className="min-h-0 overflow-y-auto px-5 py-6">
        <Drawer.Title className="text-xl font-bold leading-tight">
          {event.title}
        </Drawer.Title>
        <div className="mt-5 space-y-3 text-sm">
          <div>
            <p className="font-medium">Applies to</p>
            <p className="text-muted-foreground">{event.user.name}</p>
          </div>
          <div>
            <p className="font-medium">Start</p>
            <p className="text-muted-foreground">
              {format(
                zonedEvent(event.startDate, timeZone),
                'MMM d, yyyy h:mm a'
              )}
            </p>
          </div>
          <div>
            <p className="font-medium">End</p>
            <p className="text-muted-foreground">
              {format(
                zonedEvent(event.endDate, timeZone),
                'MMM d, yyyy h:mm a'
              )}
            </p>
          </div>
          {event.description && (
            <div>
              <p className="font-medium">Description</p>
              <p className="text-muted-foreground">{event.description}</p>
            </div>
          )}
        </div>
        <p className="mt-5 text-xs text-muted-foreground">
          {event.metadata?.calendarSourceId
            ? 'Make changes in the calendar where this time was booked.'
            : 'This event was set up in Google Calendar. This cannot be modified in Borrah, make changes in Google Calendar.'}
        </p>
        <Button className="mt-6 w-full" onClick={onClose}>
          Close
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="px-5 pt-2 pb-3">
        <Drawer.Title className="text-xl font-bold leading-tight">
          {event.title}
        </Drawer.Title>
        <Badge variant="outline" className="mt-1 rounded-full">
          Blocked
        </Badge>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">
        <MobileBlockedTimeForm
          formId={formId}
          editing={blockedTimeTargetFromEvent(event)}
          onPendingChange={setIsSaving}
          onSaved={onClose}
          onDeleted={onClose}
        />
      </div>

      <div className="border-t border-border px-5 pt-3 pb-2">
        <Button
          type="submit"
          form={formId}
          disabled={isSaving}
          className="h-12 w-full rounded-full"
        >
          {isSaving ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <SaveIcon className="size-4" />
          )}
          {isSaving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// Appointment — explicit save + status / quick actions / checkout
// =============================================================================

function AppointmentBody({
  event,
  onClose,
}: {
  event: IEvent;
  onClose: () => void;
}) {
  const { users, config, timeZone } = useCalendar();
  const [colorBy] = useCalendarColorBy();
  const { updateEvent } = useUpdateEvent();
  const openCheckout = useCheckoutStore((s) => s.openCheckout);

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const startDate = zonedEvent(event.startDate, timeZone);
  const context = { users, timeZone };
  const durationMinutes = appointmentEventDuration(event, timeZone);

  // Live, not the snapshot the sheet opened with: a deposit payment confirms
  // the booking while it is open (ENG-1306).
  const { status, setStatus, deposit } = useLiveBooking(event);
  const serviceName =
    (event.metadata?.serviceName as string | undefined) ?? event.title;

  const clientName =
    typeof event.metadata?.leadName === 'string' ? event.metadata.leadName : '';
  const leadId =
    typeof event.metadata?.leadId === 'string' ? event.metadata.leadId : '';

  // Keep the currently-assigned person selectable even if they're no longer in
  // the active practitioner list. Same resolution as the desktop dialog.
  const staffOptions = appointmentEditStaffOptions(event, users);
  const bookedStaff = users.find(
    (user) => user.id === event.metadata?.practitionerId
  );
  const staffName =
    bookedStaff?.name ??
    (typeof event.metadata?.staffMemberName === 'string'
      ? event.metadata.staffMemberName
      : event.user.name);
  const staffPicture =
    bookedStaff?.picturePath ??
    (event.metadata?.practitionerId ? null : event.user.picturePath);

  const form = useForm<AppointmentEditFormData>({
    resolver: zodResolver(appointmentEditSchema),
    defaultValues: appointmentEditDefaults(event, context),
  });

  /**
   * Explicit save. `updateEvent` reverts its optimistic update and RE-THROWS on
   * a rejected save, so we must catch: surface the failure and keep the sheet
   * open with the user's edits rather than pretending the change stuck.
   */
  const onSubmit = async (data: AppointmentEditFormData) => {
    if (!form.formState.isDirty) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      await updateEvent(applyAppointmentEdit(event, data, context));
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to save changes'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!config.onDeleteEvent) return;
    setIsDeleting(true);
    try {
      await config.onDeleteEvent(event);
      onClose();
    } catch (error) {
      reportDeleteEventFailure(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const goToCheckout = () => {
    onClose();
    openCheckout({
      appointmentId: String(event.id),
      appointmentName: serviceName,
    });
  };

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex min-h-0 flex-1 flex-col"
    >
      <div className="flex items-start justify-between gap-3 px-5 pt-2 pb-3">
        <div className="min-w-0 flex-1">
          <Drawer.Title className="truncate text-xl font-bold leading-tight">
            {event.title}
          </Drawer.Title>
          {clientName && (
            <p className="mt-2 text-sm">
              {leadId ? (
                <AppointmentClientLink
                  leadId={leadId}
                  name={clientName}
                  onNavigate={onClose}
                  className="inline-flex min-h-11 items-center"
                />
              ) : (
                clientName
              )}
            </p>
          )}
          <p className="mt-1 text-sm text-muted-foreground">
            {format(startDate, 'EEE d MMM')} · {format(startDate, 'HH:mm')} ·{' '}
            {durationMinutes} min
          </p>
          {/* Under the date, not beside the title: a long deposit badge
              ("Deposit link not created") beside it squeezed the title to a
              few letters and wrapped the date on a phone. */}
          {(status || deposit) && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {status && (
                <Badge variant="outline" className="rounded-full">
                  {appointmentStatusLabels[status] ?? status}
                </Badge>
              )}
              <DepositBadge deposit={deposit} />
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <AppointmentQuickActions
            appointmentId={String(event.id)}
            status={status ?? 'booked'}
            startDate={event.startDate}
            endDate={event.endDate}
            deposit={deposit}
            onDone={onClose}
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 pb-4">
        <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2">
          <Avatar className="size-10">
            {staffPicture && <AvatarImage src={staffPicture} alt={staffName} />}
            <AvatarFallback className="text-xs">
              {staffName
                .split(' ')
                .map((p) => p[0])
                .slice(0, 2)
                .join('')
                .toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{serviceName}</p>
            <p className="text-xs text-muted-foreground">{staffName}</p>
          </div>
          <span
            className={cn(
              'ml-auto h-8 w-1 shrink-0 rounded-full',
              colorBar(getCalendarEventColor(event, colorBy))
            )}
            aria-hidden
          />
        </div>

        <DepositDetailRow deposit={deposit} />
        <DepositLinkActions appointmentId={String(event.id)} />

        {status && (
          <AppointmentStatusProgression
            appointmentId={String(event.id)}
            status={status}
            onLocalChange={setStatus}
            deposit={deposit}
            startDate={event.startDate}
          />
        )}

        <AppointmentEditFields
          control={form.control}
          staff={staffOptions}
          currentDurationMinutes={durationMinutes}
          currentServiceName={
            typeof event.metadata?.serviceName === 'string'
              ? event.metadata.serviceName
              : event.title
          }
          className="space-y-5"
        />

        <AppointmentFormsPanel appointmentId={String(event.id)} />

        {config.onDeleteEvent && (
          <ConfirmDeleteDialog
            cancelLabel="Keep"
            confirmLabel="Cancel booking"
            description="The booking is removed from the calendar and the client’s history. This can’t be undone."
            isPending={isDeleting}
            onConfirm={handleDelete}
            title="Cancel this booking?"
            trigger={
              <button
                className="flex w-full items-center justify-center gap-2 rounded-full border border-destructive/30 py-3 text-sm font-semibold text-destructive active:bg-destructive/5 disabled:opacity-50"
                disabled={isDeleting}
                type="button"
              >
                {isDeleting ? 'Cancelling…' : 'Cancel booking'}
              </button>
            }
          />
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-border px-5 pt-3 pb-2">
        <Button
          type="button"
          variant="outline"
          className="h-12 flex-1 rounded-full"
          onClick={goToCheckout}
        >
          <CreditCardIcon className="size-4" />
          Checkout
        </Button>
        <Button
          type="submit"
          className="h-12 flex-1 rounded-full"
          disabled={!form.formState.isDirty || isSaving}
        >
          {isSaving ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <SaveIcon className="size-4" />
          )}
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

function colorBar(color: IEvent['color']): string {
  switch (color) {
    case 'blue':
      return 'bg-blue-500';
    case 'green':
      return 'bg-green-500';
    case 'red':
      return 'bg-red-500';
    case 'yellow':
      return 'bg-yellow-500';
    case 'purple':
      return 'bg-purple-500';
    case 'orange':
      return 'bg-orange-500';
    case 'gray':
      return 'bg-gray-400';
    default:
      return 'bg-muted-foreground/40';
  }
}
